import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import {
  ImageGenerationJob,
  JobImage,
  JobDetailResponse,
} from '../../services/api/imageGenerationService';
import imageGenerationService from '../../services/api/imageGenerationService';
import {
  X,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  AlertCircle,
  RefreshCw,
  Video,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from 'lucide-react';
import AdScoreHistogram from './AdScoreHistogram';
import { usePolling } from '../../hooks/usePolling';

interface JobDetailModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onJobCancelled?: () => void;
  onRegenerate?: (job: ImageGenerationJob) => void;
}

const JobDetailModal: React.FC<JobDetailModalProps> = ({
  jobId,
  isOpen,
  onClose,
  onJobCancelled,
  onRegenerate,
}) => {
  const navigate = useNavigate();
  const organization = useSelector(selectOrganization);
  const adScoreEnabled = organization?.enable_ad_score ?? false;
  const [job, setJob] = useState<ImageGenerationJob | null>(null);
  const [images, setImages] = useState<JobImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [histogramOpen, setHistogramOpen] = useState(false);

  const loadJobDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      setError(null);
      const response: JobDetailResponse = await imageGenerationService.getJobDetail(jobId);
      setJob(response.job);
      setImages(response.images);
    } catch (err) {
      setError('ジョブの詳細を取得できませんでした');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // 進行中のジョブだけ更新を追う。404/403 などで止まり、非表示タブでは取得しない。
  const isJobActive = job?.status === 'pending' || job?.status === 'generating_prompt' || job?.status === 'processing';
  const { run: fetchJobDetail } = usePolling(loadJobDetail, {
    enabled: isOpen && !!jobId && isJobActive,
    intervalMs: 3000,
  });

  useEffect(() => {
    if (!isOpen || !jobId) return;
    fetchJobDetail();
  }, [isOpen, jobId, fetchJobDetail, loadJobDetail]);

  const handleCancel = async () => {
    if (!job || cancelling) return;

    try {
      setCancelling(true);
      await imageGenerationService.cancelJob(job.id);
      onJobCancelled?.();
      onClose();
    } catch (err) {
      console.error('Failed to cancel job:', err);
      setError('ジョブのキャンセルに失敗しました');
    } finally {
      setCancelling(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 75) return { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' };
    if (score >= 50) return { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500' };
    return { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' };
  };

  const getScoreStars = (score: number) => {
    const filledStars = Math.round(score / 20); // 0-100 -> 0-5 stars
    return Array.from({ length: 5 }, (_, i) => (i < filledStars ? '\u2605' : '\u2606')).join('');
  };

  const handleNavigateToVideo = (image: JobImage) => {
    onClose();
    navigate('/ads/video', {
      state: {
        refImageS3Key: image.s3_key,
        refPrompt: job?.prompt,
        preferredStyle: 'バナー動画',
      },
    });
  };

  const getStatusBadge = () => {
    if (!job) return null;

    const statusConfig: Record<string, { icon: React.ReactNode; text: string; bgColor: string; textColor: string }> = {
      pending: {
        icon: <Clock className="w-4 h-4" />,
        text: '待機中',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
      },
      generating_prompt: {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: 'AIでプロンプト生成中...',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
      },
      processing: {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: '処理中',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
      },
      completed: {
        icon: <CheckCircle className="w-4 h-4" />,
        text: '完了',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
      },
      failed: {
        icon: <XCircle className="w-4 h-4" />,
        text: '失敗',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
      },
    };

    const config = statusConfig[job.status];
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}
      >
        {config.icon}
        {config.text}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {job?.input_mode === 'magic_resize' ? 'マジックリサイズ詳細' : 'ジョブ詳細'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-130px)]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8 text-red-500">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            ) : job ? (
              <div className="space-y-6">
                {/* Status and Info */}
                <div className="flex items-center justify-between">
                  {getStatusBadge()}
                  <span className="text-sm text-gray-500">
                    {new Date(job.created_at).toLocaleString('ja-JP')}
                  </span>
                </div>

                {/* Progress */}
                {(job.status === 'pending' || job.status === 'generating_prompt' || job.status === 'processing') && (
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>進捗</span>
                      <span>
                        {job.completed_count}/{job.count}枚完了
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Prompt - Only shown for freetext mode jobs */}
                {job.input_mode === 'freetext' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">プロンプト</h3>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap">
                      {job.prompt}
                    </div>
                  </div>
                )}

                {/* Generation Info */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" />
                    <span>{job.count}{job.input_mode === 'magic_resize' ? 'サイズ' : '枚'}生成</span>
                  </div>
                  {job.completed_count > 0 && (
                    <span className="text-green-600">{job.completed_count}枚成功</span>
                  )}
                </div>

                {/* Error Message */}
                {job.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <div className="text-sm text-red-700">{job.error_message}</div>
                    </div>
                  </div>
                )}

                {/* Generated Images */}
                {images.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">生成された画像</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {images.map((image) => (
                        <div key={image.id} className="space-y-2">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={image.url}
                              alt="Generated image"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          {/* Ad Score Badge */}
                          {adScoreEnabled && (image.ad_score != null ? (
                            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${getScoreColor(image.ad_score).bg} ${getScoreColor(image.ad_score).text}`}>
                              <span className="tracking-tight">{getScoreStars(image.ad_score)}</span>
                              <span>{image.ad_score}点</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-400">
                              スコア未計算
                            </div>
                          ))}
                          {/* Navigate to Video Button */}
                          {job?.status === 'completed' && (
                            <button
                              onClick={() => handleNavigateToVideo(image)}
                              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 transition-colors"
                            >
                              <Video className="w-3.5 h-3.5" />
                              動画にする
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ad Score Distribution Histogram */}
                {adScoreEnabled && images.some((img) => img.ad_score != null) && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setHistogramOpen(!histogramOpen)}
                      aria-expanded={histogramOpen}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <BarChart3 className="w-4 h-4" />
                        効果予測分布
                      </div>
                      {histogramOpen ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    {histogramOpen && (
                      <div className="px-4 py-4">
                        <AdScoreHistogram
                          predictedObservations={
                            images.find((img) => img.predicted_observations != null)
                              ?.predicted_observations ?? undefined
                          }
                          predictedPlays={
                            images.find((img) => img.predicted_plays != null)
                              ?.predicted_plays ?? undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Cancel Button */}
                {(job.status === 'pending' || job.status === 'generating_prompt') && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {cancelling ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          キャンセル中...
                        </span>
                      ) : (
                        'ジョブをキャンセル'
                      )}
                    </button>
                  </div>
                )}

                {/* Regenerate Button - Show when job failed */}
                {job.status === 'failed' && onRegenerate && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        onRegenerate(job);
                        onClose();
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      同じ設定で再生成
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;
